import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CategoryProvider } from './context/CategoryContext';
import { DashboardProvider } from './context/DashboardContext';
import { NotificationProvider } from './context/NotificationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { FavoritesProvider } from './context/FavoritesContext';
import AdminLayout from './layouts/AdminLayout';

// Import des styles MapLibre GL JS
import 'maplibre-gl/dist/maplibre-gl.css';

// Import react-toastify
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import CategoriesPage from './pages/CategoriesPage';
import CategoryDetailPage from './pages/CategoryDetailPage';
import AdminCategoriesPage from './pages/AdminCategoriesPage';
import CategoryTranslations from './components/admin/CategoryTranslations';
import AdminListingFieldsPage from './pages/admin/AdminListingFieldsPage';

import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ActivitySuspensionPage from './pages/ActivitySuspensionPage';
import ProfilePage from './pages/ProfilePage';
import MyRequestsPage from './pages/MyRequestsPage';
import FavoritesPage from './pages/FavoritesPage';

// Import des composants de listings
import CreateListingForm from './components/listing/CreateListingForm';
import MyListings from './components/listing/MyListings';
// ─── Import Marketplace Produits ───────────────────────────────────────────────
import CreateProductForm from './components/product/CreateProductForm';
import MyProductsPage from './pages/products/MyProductsPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import ProductOrderPage from './pages/products/ProductOrderPage';
import ListingDetail from './components/listing/ListingDetail';

// Import de la nouvelle page de visualisation publique
import ListingViewPage from './pages/ListingViewPage';
import OwnerProfilePage from './pages/OwnerProfilePage';

// Import des composants de réservation
import ReservationPage from './pages/reservation/ReservationPage';
import BookingConfirmation from './components/booking/BookingConfirmation';
import ReservationDetailPage from './pages/ReservationDetailPage';

// Import des pages d'administration
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUserRolesPage from './components/admin/UserRoleManagement';
import AdminRolesPage from './components/admin/RoleManagement';
import AdminUsersPage from './components/admin/UserManagement'; // Ajout de l'import pour la gestion des utilisateurs
import PermissionManagement from './components/admin/PermissionManagement';
import CategoryManagement from './components/dashboard/CategoryManagementTab';
import ListingValidation from './components/admin/ListingValidation';
import ListingHistory from './components/admin/ListingHistory';
import ListingHistoryPage from './pages/admin/ListingHistoryPage'; // Nouvelle page d'historique
import ListingStats from './components/admin/ListingStats';
import AllListings from './components/admin/AllListings';

// PHASE 5: Import des nouvelles interfaces admin complètes
import AdminReservations from './pages/admin/AdminReservations';
import AdminFinances from './pages/admin/AdminFinances';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminPayoutsPage from './pages/admin/AdminPayoutsPage';
import AdminPayinsPage from './pages/admin/AdminPayinsPage';
import AdminCommissionInvoiceDetailPage from './pages/admin/AdminCommissionInvoiceDetailPage';
import PayoutDeductionsAdminPage from './pages/admin/PayoutDeductionsAdminPage';
import ReviewModerationPage from './pages/admin/ReviewModerationPage';
import OwnerRenterReviewModerationPage from './pages/admin/OwnerRenterReviewModerationPage';
import ResponseModerationPage from './pages/admin/ResponseModerationPage';
import AdminDisputesPage from './pages/admin/AdminDisputesPage';
import Admin3DSManagementPage from './pages/admin/Admin3DSManagementPage';
import AdminCashSettlementPage from './pages/admin/AdminCashSettlementPage';
// Import des pages User ↔ Listings
import UserListingsPage from './pages/admin/UserListingsPage';
import UserListingsSearchPage from './pages/admin/UserListingsSearchPage';
import UserListingsStatsPage from './pages/admin/UserListingsStatsPage';
import UserListingsBulkActionsPage from './pages/admin/UserListingsBulkActionsPage';
import UserListingsEmailPage from './pages/admin/UserListingsEmailPage';
import UserListingDetailPage from './pages/admin/UserListingDetailPage';
// Import de la page admin Restrictions/Bans/Strikes
import UserRestrictionsPage from './pages/admin/UserRestrictionsPage';
// Import de la page de gestion des appels de ban
import BanAppealsPage from './pages/admin/BanAppealsPage';

// Debug Components
import SSLTestComponent from './components/debug/SSLTestComponent';
import UserListingEditPage from './pages/admin/UserListingEditPage';

// Import des composants de notification
import NotificationToast from './components/notifications/NotificationToast';
import NotificationsPage from './pages/NotificationsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import NotificationManagement from './pages/admin/NotificationManagement';

// Import du composant de bannière de restriction
import RestrictionBannerWrapper from './components/common/RestrictionBannerWrapper';
// Import de la page de compte restreint
import AccountRestrictedPage from './pages/AccountRestrictedPage';

// Import Dispute Detail Page
import DisputeDetailPage from './pages/DisputeDetailPage';
import AllDisputesPage from './pages/AllDisputesPage';
import MyPenaltiesPage from './pages/MyPenaltiesPage';
import PenaltyDetailPage from './pages/PenaltyDetailPage';

// Import du Dashboard Monitoring
import MonitoringDashboard from './pages/admin/MonitoringDashboard';
import GeocodingMonitoringPage from './pages/admin/GeocodingMonitoringPage';

// Import du Dashboard Unifié (remplace l'ancien OwnerDashboardPage)
import DashboardUnifiedPage from './pages/DashboardUnifiedPage';
import DashboardSelectionPage from './pages/DashboardSelectionPage';
import RenterDashboardPage from './pages/RenterDashboardPage';



// Import des nouvelles pages financières autonomes
import RenterFinancialPage from './pages/RenterFinancialPage';
import OwnerFinancialPage from './pages/OwnerFinancialPage';

// Import des pages owner spécifiques
import CommissionPaymentSuccessPage from './pages/owner/CommissionPaymentSuccessPage';
import OwnerInvoicesPage from './pages/owner/OwnerInvoicesPage';
import OwnerInvoiceDetailPage from './pages/owner/OwnerInvoiceDetailPage';
import OwnerPayoutsPage from './pages/owner/OwnerPayoutsPage';

// Import de la page d'administration des jobs automatisés
import AutomatedJobsAdmin from './pages/admin/AutomatedJobsAdmin';
// Import de la page de gestion de la configuration système
import AdminConfigPage from './pages/admin/AdminConfigPage';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import theme from './theme';
import ProtectedRoute from './components/common/ProtectedRoute';
import OrderProtectedRoute from './components/common/OrderProtectedRoute';
// Nouvelles pages pour la gestion des rôles
import RoleList from './pages/admin/roles/RoleList';
import RoleForm from './pages/admin/roles/RoleForm';
import RolePermissions from './pages/admin/roles/RolePermissions';
import RoleUsers from './pages/admin/roles/RoleUsers';

// Nouvelles pages de réservations autonomes
import RenterReservationsStandalonePage from './pages/RenterReservationsStandalonePage';
import OwnerReservationsStandalonePage from './pages/OwnerReservationsStandalonePage';
import PERMISSIONS from './config/permissionsList'; // Ajustez le chemin si nécessaire

// Module Support
import SupportFaqPage from './pages/SupportFaqPage';
import SupportNewTicketPage from './pages/SupportNewTicketPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import SupportTicketDetailPage from './pages/SupportTicketDetailPage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AgentDashboardPage from './pages/AgentDashboardPage';
import HelpWidget from './components/support/HelpWidget';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';

// Pages légales
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CookiesPage from './pages/legal/CookiesPage';

/**
 * Composant principal de l'application
 * @returns {React.ReactNode} - Composant principal
 */
function App() {
    return (
        <Suspense fallback={<div>Loading...</div>}>
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <CurrencyProvider>
                    <DashboardProvider>
                        <NotificationProvider>
                            <CategoryProvider>
                            <FavoritesProvider>
                            <Router>
                                <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                                    <Header />
                                    <RestrictionBannerWrapper />
                                    <NotificationToast />
                                    <HelpWidget />
                                    <main style={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
                                        <Routes>
                                            {/* Routes publiques */}
                                            <Route path="/" element={<HomePage />} />
                                            <Route path="/search" element={<SearchPage />} />
                                            <Route path="/how-it-works" element={<HowItWorksPage />} />
                                            <Route path="/about" element={<AboutPage />} />

                                            {/* Routes Support - publiques */}
                                            <Route path="/support/faq" element={<SupportFaqPage />} />
                                            <Route path="/support/faq/:idOrSlug" element={<SupportFaqPage />} />
                                            <Route path="/support/new" element={<SupportNewTicketPage />} />
                                            <Route path="/support/ticket" element={<SupportTicketDetailPage />} />
                                            <Route path="/login" element={<LoginPage />} />
                                            <Route path="/register" element={<RegisterPage />} />
                                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                            <Route path="/categories" element={<CategoriesPage />} />
                                            <Route path="/categories/:idOrSlug" element={<CategoryDetailPage />} />

                                            {/* Routes légales */}
                                            <Route path="/terms" element={<TermsPage />} />
                                            <Route path="/privacy" element={<PrivacyPage />} />
                                            <Route path="/cookies" element={<CookiesPage />} />
                                            
                                            {/* Route de compte restreint/banni */}
                                            <Route path="/account-restricted" element={<AccountRestrictedPage />} />
                                            
                                            {/* Route de réservation - nécessite authentification */}
                                            <Route
                                                path="/reservation/:listingId"
                                                element={
                                                    <ProtectedRoute>
                                                        <ReservationPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                           
                                            {/* Route de détails de réservation - accessible via email */}
                                            <Route
                                                path="/reservations/:id"
                                                element={
                                                    <ProtectedRoute>
                                                        <ReservationDetailPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                           
                                            {/* Route de confirmation de réservation */}
                                            <Route
                                                path="/booking/confirmation/:reservationId"
                                                element={
                                                    <ProtectedRoute>
                                                        <BookingConfirmation />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            
                                            {/* Route de debug SSL */}
                                            <Route path="/debug/ssl-test" element={<SSLTestComponent />} />

                                            {/* Routes protégées par authentification */}
                                            <Route
                                                path="/profile"
                                                element={
                                                    <ProtectedRoute>
                                                        <ProfilePage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/favorites"
                                                element={
                                                    <ProtectedRoute>
                                                        <FavoritesPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/renter/favorites"
                                                element={
                                                    <ProtectedRoute>
                                                        <FavoritesPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/my-requests"
                                                element={
                                                    <ProtectedRoute>
                                                        <MyRequestsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Routes de notifications */}
                                            <Route
                                                path="/notifications"
                                                element={
                                                    <ProtectedRoute>
                                                        <NotificationsPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/notifications/preferences"
                                                element={
                                                    <ProtectedRoute>
                                                        <NotificationPreferencesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Route de détails de litige */}
                                            <Route
                                                path="/disputes/:disputeId"
                                                element={
                                                    <ProtectedRoute>
                                                        <DisputeDetailPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Liste des litiges de l'utilisateur */}
                                            <Route
                                                path="/disputes"
                                                element={
                                                    <ProtectedRoute>
                                                        <AllDisputesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Liste des pénalités de l'utilisateur */}
                                            <Route
                                                path="/penalties"
                                                element={
                                                    <ProtectedRoute>
                                                        <MyPenaltiesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Route de détail de pénalité */}
                                            <Route
                                                path="/penalties/:penaltyId"
                                                element={
                                                    <ProtectedRoute>
                                                        <PenaltyDetailPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Nouvelles routes financières autonomes */}
                                            <Route
                                                path="/financial-renter"
                                                element={
                                                    <ProtectedRoute>
                                                        <RenterFinancialPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/financial-owner"
                                                element={
                                                    <ProtectedRoute>
                                                        <OwnerFinancialPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Routes des listings */}
                                            <Route
                                                path="/create-listing"
                                                element={
                                                    <ProtectedRoute>
                                                        <CreateListingForm />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/edit-listing/:id"
                                                element={
                                                    <ProtectedRoute>
                                                        <CreateListingForm />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/my-listings"
                                                element={
                                                    <ProtectedRoute>
                                                        <MyListings />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* ─── Routes Marketplace Produits ─── */}
                                            <Route
                                                path="/create-product"
                                                element={
                                                    <ProtectedRoute>
                                                        <CreateProductForm />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/edit-product/:id"
                                                element={
                                                    <ProtectedRoute>
                                                        <CreateProductForm />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/my-products"
                                                element={
                                                    <ProtectedRoute>
                                                        <MyProductsPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/activity-suspension"
                                                element={
                                                    <ProtectedRoute>
                                                        <ActivitySuspensionPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/product/:id"
                                                element={
                                                    <ProtectedRoute>
                                                        <ProductDetailPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/product/:id/order"
                                                element={
                                                    <OrderProtectedRoute
                                                        title={undefined}
                                                        message={undefined}
                                                    >
                                                        <ProductOrderPage />
                                                    </OrderProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/listing/:id"
                                                element={<ListingDetail />}
                                            />
                                            <Route
                                                path="/view-listing/:id"
                                                element={
                                                    <OrderProtectedRoute
                                                        title={undefined}
                                                        message={undefined}
                                                    >
                                                        <ListingViewPage />
                                                    </OrderProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/owner/:ownerId"
                                                element={<OwnerProfilePage />}
                                            />

                                            {/* Dashboard - Page de sélection */}
                                            <Route
                                                path="/my-dashboard"
                                                element={
                                                    <ProtectedRoute>
                                                        <DashboardSelectionPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Nouvelles routes de réservations autonomes */}
                                            <Route
                                                path="/reservations-renter"
                                                element={
                                                    <ProtectedRoute>
                                                        <RenterReservationsStandalonePage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/reservations-owner"
                                                element={
                                                    <ProtectedRoute>
                                                        <OwnerReservationsStandalonePage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Dashboard Auto (fallback) */}
                                            <Route
                                                path="/dashboard/auto"
                                                element={
                                                    <ProtectedRoute>
                                                        <DashboardUnifiedPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* Ancien Dashboard - à conserver (informations personnelles) */}
                                            <Route
                                                path="/dashboard"
                                                element={
                                                    <ProtectedRoute>
                                                        <DashboardPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                            {/* Redirection de l'ancienne route vers la nouvelle */}
                            <Route
                                path="/owner/dashboard"
                                element={<Navigate to="/dashboard" replace />}
                            />

                            {/* Routes owner spécifiques */}
                            <Route
                                path="/owner/financial/commission-payment-success"
                                element={
                                    <ProtectedRoute>
                                        <CommissionPaymentSuccessPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/owner/invoices"
                                element={
                                    <ProtectedRoute>
                                        <OwnerInvoicesPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/owner/invoices/:id"
                                element={
                                    <ProtectedRoute>
                                        <OwnerInvoiceDetailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/owner/payouts"
                                element={
                                    <ProtectedRoute>
                                        <OwnerPayoutsPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Routes Support - authentifiées */}
                            <Route
                                path="/support/tickets"
                                element={
                                    <ProtectedRoute>
                                        <SupportTicketsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/support/tickets/:id"
                                element={
                                    <ProtectedRoute>
                                        <SupportTicketDetailPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Agent Dashboard */}
                            <Route
                                path="/agent-dashboard"
                                element={
                                    <ProtectedRoute>
                                        <AgentDashboardPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Routes d'administration avec layout partagé */}
                                            <Route
                                                path="/admin/*"
                                                element={
                                                    <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                        <AdminLayout />
                                                    </ProtectedRoute>
                                                }
                                            >
                                                <Route index element={<AdminDashboard />} />

                                                <Route
                                                    path="categories"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_CATEGORIES}>
                                                            <AdminCategoriesPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="category-translations"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_CATEGORIES}>
                                                            <CategoryTranslations />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="listing-fields"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_CATEGORIES}>
                                                            <AdminListingFieldsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="users"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_USER}>
                                                            <AdminUsersPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-roles"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES} >
                                                            <AdminUserRolesPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="roles"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <AdminRolesPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="roles/create"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <RoleForm />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="roles/edit/:id"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <RoleForm />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="roles/:id/permissions"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <RolePermissions />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="roles/:id/users"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <RoleUsers />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route de gestion des permissions */}
                                                <Route
                                                    path="permissions"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_ROLES}>
                                                            <PermissionManagement />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route de gestion des notifications */}
                                                <Route
                                                    path="notifications"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_NOTIFICATION}>
                                                            <NotificationManagement />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route du Dashboard Monitoring */}
                                                <Route
                                                    path="monitoring"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <MonitoringDashboard />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route du Monitoring Géocodage */}
                                                <Route
                                                    path="geocoding-monitoring"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <GeocodingMonitoringPage />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Routes de gestion des listings */}
                                                <Route
                                                    path="all-listings"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_PENDING_LISTINGS}>
                                                            <AllListings />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="listings"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_PENDING_LISTINGS}>
                                                            <ListingValidation />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="listings/history"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_LISTING_HISTORY}>
                                                            <ListingHistoryPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="listings/stats"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_PENDING_LISTINGS}>
                                                            <ListingStats />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Routes User ↔ Listings Management */}
                                                <Route
                                                    path="user-listings"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_USER_LISTING}>
                                                            <UserListingsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/search"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_USER_LISTING}>
                                                            <UserListingsSearchPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/stats"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_USER_LISTING_STATS}>
                                                            <UserListingsStatsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/bulk-actions"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.BULK_USER_LISTING_ACTIONS}>
                                                            <UserListingsBulkActionsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/email"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.SEND_USER_LISTING_EMAIL}>
                                                            <UserListingsEmailPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/:userId/:listingId"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <UserListingDetailPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="user-listings/edit/:userId/:listingId"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <UserListingEditPage />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route de gestion des jobs automatisés */}
                                                <Route
                                                    path="automated-jobs"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_AUTOMATED_JOBS}>
                                                            <AutomatedJobsAdmin />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route de gestion de la configuration système */}
                                                <Route
                                                    path="config"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_SYSTEM}>
                                                            <AdminConfigPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="reviews/moderation"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_REVIEWS}>
                                                            <ReviewModerationPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="reviews/owner-renter-moderation"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_REVIEWS}>
                                                            <OwnerRenterReviewModerationPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="reviews/response-moderation"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_REVIEWS}>
                                                            <ResponseModerationPage />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* Route de gestion des litiges */}
                                                <Route
                                                    path="disputes"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_DISPUTES}>
                                                            <AdminDisputesPage />
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* PHASE 5: Routes Admin Complètes */}
                                                <Route
                                                    path="reservations"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_RESERVATIONS}>
                                                            <AdminReservations />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="finances"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_PAYMENTS}>
                                                            <AdminFinances />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="payins"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_PAYMENTS}>
                                                            <AdminPayinsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="payins/commissions/:id"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_PAYMENTS}>
                                                            <AdminCommissionInvoiceDetailPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="payouts"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_PAYMENTS}>
                                                            <AdminPayoutsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="payout-deductions"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.MANAGE_PAYMENTS}>
                                                            <PayoutDeductionsAdminPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="analytics"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <AdminAnalytics />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="restrictions"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_RESTRICTIONS}>
                                                            <UserRestrictionsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="appeals"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.VIEW_RESTRICTIONS}>
                                                            <BanAppealsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="support/*"
                                                    element={
                                                        <ProtectedRoute permission={PERMISSIONS.ACCESS_ADMIN_PANEL}>
                                                            <AdminSupportPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                            </Route>

                                            {/* Redirection par défaut */}
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Routes>
                                    </main>
                                    <Footer />
                                </div>
                            </Router>
                            </FavoritesProvider>
                            </CategoryProvider>
                        </NotificationProvider>
                    </DashboardProvider>
                </CurrencyProvider>
            </AuthProvider>
        </ThemeProvider>
        {/* Container pour les notifications toast */}
        <ToastContainer
            position="top-center"
            autoClose={6000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            style={{
                fontSize: '16px',
                zIndex: 9999
            }}
            toastStyle={{
                minHeight: '60px',
                padding: '16px'
            }}
        />
        </Suspense>
    );
}

export default App;